import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getRefereesAllPages } from '@/api/TournamentManagement/refereeApi';
import { getCompensationReportAllPages } from '@/api/TournamentManagement/workPointApi';
import type { RefereeView, CompensationReportView } from '@/models/tournament';
import { RefereeCertification } from '@/models/tournament';
import { formatLocalDateParam } from '@/utils/apiDate';

export interface RefereeWorkloadRow {
  userId: string;
  name: string;
  certification: RefereeCertification;
  rating: number;
  matchCount: number;
  workPoints: number;
}

export interface UseRefereeWorkloadResult {
  data: RefereeWorkloadRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// NEVER toISOString() for a date param — east of UTC it resolves to the previous day.
function getDefaultRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    start: formatLocalDateParam(start),
    end: formatLocalDateParam(now),
  };
}

export function useRefereeWorkload(dateRange?: { start: string; end: string }): UseRefereeWorkloadResult {
  const [referees, setReferees] = useState<RefereeView[]>([]);
  const [report, setReport] = useState<CompensationReportView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  // The two setError fallbacks below were hardcoded English — the only untranslated error strings
  // in the module, so an Arabic or French user got an English sentence in a red panel. Held in a
  // ref so a language switch does not re-key the effect and refire both network calls.
  const { t } = useTranslation('tournament');
  const tRef = useRef(t);
  tRef.current = t;

  const range = useMemo(() => dateRange ?? getDefaultRange(), [dateRange?.start, dateRange?.end]);

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setLoading(true);
      setError(null);

      try {
        const [refResult, reportResult] = await Promise.all([
          // All pages. `pageSize: 500` did NOT do that — the server clamps to 100, so every
          // referee past the 100th silently vanished from the workload table.
          getRefereesAllPages(),
          // All pages — a single 25-line page would silently show 0 matches / 0 points for
          // everyone past the first page.
          getCompensationReportAllPages({ startDate: range.start, endDate: range.end }),
        ]);

        if (cancelled) return;

        if (refResult.errors.length > 0) {
          setError(refResult.errors[0]?.description || tRef.current('Failed to load officials'));
          return;
        }

        setReferees(refResult.payload?.items ?? []);
        setReport(reportResult.errors.length === 0 ? reportResult.payload : null);
      } catch {
        if (!cancelled) {
          setError(tRef.current('Failed to load workload data'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, [fetchKey, range.start, range.end]);

  const data = useMemo<RefereeWorkloadRow[]>(() => {
    if (referees.length === 0) return [];

    const reportMap = new Map<string, { matchCount: number; workPoints: number }>();
    if (report) {
      for (const line of report.lines) {
        reportMap.set(line.refereeUserId, {
          matchCount: line.matchCount,
          workPoints: line.totalWorkPoints,
        });
      }
    }

    return referees.map((ref) => {
      const refData = reportMap.get(ref.userId);
      return {
        userId: ref.userId,
        name: ref.name,
        certification: ref.certification,
        rating: ref.rating,
        matchCount: refData?.matchCount ?? 0,
        workPoints: refData?.workPoints ?? 0,
      };
    });
  }, [referees, report]);

  const refetch = () => setFetchKey((k) => k + 1);

  return { data, loading, error, refetch };
}
